import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, computed, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DisplaySlotDto, LayerDefinitionDto } from '../../../../../api/gen';
import { DisplaySlotType } from '../../../../../api/gen/model/displaySlotType';
import { DisplayState } from '../../../../../api/gen/model/displayState';
import { LayoutStyleType } from '../../../../../api/gen/model/layoutStyleType';
import { SlotEditItemComponent } from '../slot-edit-item/slot-edit-item.component';
import { MatFormField, MatLabel, MatHint } from "@angular/material/form-field";
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface SaveLayoutPayload {
  slots: Partial<DisplaySlotDto>[];
  layers?: Partial<LayerDefinitionDto>[];
  pendingImages: Map<string, File>;
  pendingLayerImages?: Map<string, File>;
  layoutStyle?: LayoutStyleType;
  transientDurationSeconds?: number;
}

@Component({
  selector: 'app-slot-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    SlotEditItemComponent,
    MatFormField,
    MatLabel,
    MatHint],
  templateUrl: './slot-list.component.html',
  styleUrls: ['./slot-list.component.scss']
})
export class SlotListComponent implements OnChanges, OnInit {

  @Input() savedSlots: DisplaySlotDto[] | null | undefined = null;
  @Input() selectedState: DisplayState | null = null;
  @Input() saving = false;
  @Input() selectedTransientDurationSeconds: number | undefined = undefined;
  @Input() layoutStyle: LayoutStyleType = LayoutStyleType.Stack;
  @Input() errorMessage: string | null = null;
  @Output() saveLayout = new EventEmitter<SaveLayoutPayload>();

  editingSlots = signal<Partial<DisplaySlotDto>[]>([]);
  editingTransientDuration = '';
  pendingImagesMap = signal<Map<number, File>>(new Map());
  readonly orderedSlots = computed(() =>
    this.editingSlots()
      .map((slot, sourceIndex) => ({
        slot,
        sourceIndex,
        trackKey: slot.id?.trim() || `draft-${sourceIndex}`
      }))
      .sort((left, right) => {
        const leftOrder = left.slot.orderNumber ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = right.slot.orderNumber ?? Number.MAX_SAFE_INTEGER;

        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }

        return left.sourceIndex - right.sourceIndex;
      })
  );

  readonly slotTypes = Object.values(DisplaySlotType);

  get heightPercentTotal(): number {
    return this.editingSlots().reduce((sum, s) => sum + (s.heightPercent ?? 0), 0);
  }

  ngOnInit(): void {
    this.errorMessage = null;
  }

  get isHeightValid(): boolean {
    // Relative layouts position slots freely, so heights need not total 100%.
    return this.layoutStyle === LayoutStyleType.Relative || this.heightPercentTotal === 100;
  }

  get hasValidSlotIds(): boolean {
    const ids = this.editingSlots().map(s => s.slotId?.trim() ?? '');
    return ids.every(id => !!id) && new Set(ids).size === ids.length;
  }

  get canSave(): boolean {
    return this.editingSlots().length > 0 && this.isHeightValid && this.hasValidSlotIds;
  }

  get saveTooltip(): string {
    if (!this.hasValidSlotIds) return 'All slots need unique, non-empty Slot IDs';
    if (!this.isHeightValid) return `Heights must total 100% (current: ${this.heightPercentTotal}%)`;
    return '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedState'] || changes['savedSlots']) {
      this.errorMessage = null;
      this.editingSlots.set(this.savedSlots ? this.savedSlots.map(s => ({ ...s })) : []);
      this.pendingImagesMap.set(new Map());
      this.editingTransientDuration = this.selectedTransientDurationSeconds !== undefined
        ? String(this.selectedTransientDurationSeconds)
        : '';
    }


  }

  getPendingFile(index: number): File | null {
    return this.pendingImagesMap().get(index) ?? null;
  }

  addSlot(): void {
    const nextOrderNumber = this.editingSlots().reduce(
      (maxOrder, slot) => Math.max(maxOrder, slot.orderNumber ?? 0),
      0
    ) + 1;

    this.editingSlots.update(slots => [
      ...slots,
      {
        slotId: '',
        orderNumber: nextOrderNumber,
        slotType: DisplaySlotType.StaticText,
        value: '',
        heightPercent: 20,
        visible: true,
        fontSize: '24',
        fontColor: '#FFFFFF',
        backgroundColor: '#1F2937'
      }
    ]);
  }

  removeSlot(index: number): void {
    this.editingSlots.update(slots => slots.filter((_, i) => i !== index));
    this.pendingImagesMap.update(map => {
      const next = new Map<number, File>();
      map.forEach((file, i) => {
        if (i < index) next.set(i, file);
        else if (i > index) next.set(i - 1, file);
      });
      return next;
    });
  }

  updateField(index: number, field: keyof DisplaySlotDto, value: unknown): void {
    this.editingSlots.update(slots => {
      const updated = [...slots];
      const current = updated[index] ?? {};
      const next: Partial<DisplaySlotDto> = { ...current, [field]: value };
      if (field === 'slotType'
        && (value === DisplaySlotType.Image || value === DisplaySlotType.ImageWithOverlayText)) {
        next.value = '';
      }
      if (field === 'slotType' && value !== DisplaySlotType.ImageWithOverlayText) {
        next.overlayText = null;
      }
      updated[index] = next;
      return updated;
    });
  }

  setPendingImage(index: number, file: File | null): void {
    console.log('Setting pending image for slot index', index, 'file:', file);
    this.pendingImagesMap.update(map => {
      const next = new Map(map);
      if (file) next.set(index, file);
      else next.delete(index);
      return next;
    });
  }

  submit(): void {
    if (!this.canSave) return;
    console.log('Submitting layout with slots:', this.editingSlots(), 'and pending images:', this.pendingImagesMap());
    this.saveLayout.emit({
      slots: this.orderedSlots().map(entry => entry.slot),
      layers: [],
      pendingImages: this.buildPendingImagesPayload(),
      transientDurationSeconds: this.editingTransientDuration ? +this.editingTransientDuration : undefined
    });
  }

  private buildPendingImagesPayload(): Map<string, File> {
    const pendingImages = new Map<string, File>();

    for (const [index, file] of this.pendingImagesMap()) {
      const slot = this.editingSlots()[index];
      const key = this.getSlotImageKey(slot);
      if (key) {
        pendingImages.set(key, file);
      }
    }
    console.log('Built pending images payload:', pendingImages);
    return pendingImages;
  }

  private getSlotImageKey(slot: Partial<DisplaySlotDto> | undefined): string | null {
    const dbId = slot?.id?.trim();
    const slotId = slot?.slotId?.trim();
    if (dbId) {
      return JSON.stringify({ id: dbId, slotId: slotId || null });
    }

    return slotId || null;
  }

}
